/**
 * OwnerReviewBanner — 14-day review prompt after the 4-role -> 3-role migration.
 *
 * Shows only when:
 *   - the current user is the organization Owner,
 *   - at least one other member has `auto_promoted_at` within 14 days
 *     (they were auto-bumped from `manager` to `administrator` by migration 024).
 *
 * The banner has two primary actions:
 *   1. "Review administrators" — expands the list in-place so the Owner can
 *      Confirm (clear flag, keep admin) or Downgrade (clear flag, revert to member).
 *   2. "Dismiss all" — silences the banner by clearing every auto_promoted_at
 *      in one request (each member still keeps whatever role they currently have).
 *
 * The server enforces Owner-only access on both endpoints (see
 * `/api/organizations/:orgId/auto-promoted` and the DELETE sibling). UI gating
 * here is defensive only.
 */
import { AlertTriangle, Check, UserMinus, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../shared/apiClient';
import './OwnerReviewBanner.css';

interface AutoPromotedMember {
  user_id: string;
  role: string;
  display_name: string | null;
  email: string | null;
  auto_promoted_at: string;
  auto_promoted_from: string | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysUntilExpiry(promotedAt: string): number {
  const ts = new Date(promotedAt).getTime();
  if (Number.isNaN(ts)) return 0;
  const expiry = ts + 14 * MS_PER_DAY;
  const remaining = Math.ceil((expiry - Date.now()) / MS_PER_DAY);
  return Math.max(0, remaining);
}

export const OwnerReviewBanner: React.FC = () => {
  const { currentOrg } = useAuth();
  const [members, setMembers] = useState<AutoPromotedMember[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orgId = currentOrg?.id ?? null;
  const isOwner = currentOrg?.role === 'owner';

  const fetchMembers = useCallback(async () => {
    if (!orgId || !isOwner) return;
    try {
      const res = await apiFetch(`/api/organizations/${orgId}/auto-promoted`, {
        headers: { 'X-Organization-Id': orgId },
      });
      if (res.status === 403 || res.status === 401) {
        setMembers([]);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { members: AutoPromotedMember[] };
      setMembers(Array.isArray(data.members) ? data.members : []);
    } catch (e) {
      // Non-fatal: banner stays hidden if the endpoint is unavailable.
      setMembers([]);
      setError(e instanceof Error ? e.message : 'Failed to load review list');
    }
  }, [orgId, isOwner]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  const pending = members ?? [];
  const minDaysRemaining = useMemo(
    () =>
      pending.length ? Math.min(...pending.map((m) => daysUntilExpiry(m.auto_promoted_at))) : 0,
    [pending]
  );

  if (!isOwner || !orgId) return null;
  if (!pending.length) return null;

  const handleSingle = async (userId: string, downgrade: boolean) => {
    if (!orgId) return;
    setBusyUserId(userId);
    setError(null);
    try {
      const path = `/api/organizations/${orgId}/members/${userId}/auto-promoted-flag${
        downgrade ? '?downgrade=1' : ''
      }`;
      const res = await apiFetch(path, {
        method: 'DELETE',
        headers: { 'X-Organization-Id': orgId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMembers((prev) => (prev ? prev.filter((m) => m.user_id !== userId) : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update member');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDismissAll = async () => {
    if (!orgId) return;
    setError(null);
    const ids = pending.map((m) => m.user_id);
    try {
      await Promise.all(
        ids.map((id) =>
          apiFetch(`/api/organizations/${orgId}/members/${id}/auto-promoted-flag`, {
            method: 'DELETE',
            headers: { 'X-Organization-Id': orgId },
          })
        )
      );
      setMembers([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to dismiss banner');
    }
  };

  return (
    <div
      role="region"
      aria-label="Role migration review"
      className="owner-review-banner"
      data-count={pending.length}
    >
      <div className="owner-review-banner__header">
        <AlertTriangle size={18} aria-hidden="true" />
        <div className="owner-review-banner__message">
          <strong>Roles simplified to 3 tiers.</strong>{' '}
          {pending.length === 1
            ? '1 member was auto-promoted to Administrator during the migration.'
            : `${pending.length} members were auto-promoted to Administrator during the migration.`}{' '}
          Review and confirm or downgrade. Reminder clears in {minDaysRemaining} day
          {minDaysRemaining === 1 ? '' : 's'}.
        </div>
        <div className="owner-review-banner__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Hide list' : 'Review administrators'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleDismissAll}
            aria-label="Dismiss role migration banner"
            title="Dismiss banner (keep all as Administrator)"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {expanded ? (
        <ul className="owner-review-banner__list">
          {pending.map((m) => {
            const label = m.display_name || m.email || m.user_id;
            const daysLeft = daysUntilExpiry(m.auto_promoted_at);
            const busy = busyUserId === m.user_id;
            return (
              <li key={m.user_id} className="owner-review-banner__item">
                <div className="owner-review-banner__identity">
                  <span className="owner-review-banner__name">{label}</span>
                  {m.email && m.display_name ? (
                    <span className="owner-review-banner__email">{m.email}</span>
                  ) : null}
                  <span className="owner-review-banner__days">
                    {daysLeft} day{daysLeft === 1 ? '' : 's'} left
                  </span>
                </div>
                <div className="owner-review-banner__row-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() => handleSingle(m.user_id, false)}
                  >
                    <Check size={14} aria-hidden="true" /> Keep as Administrator
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-danger"
                    disabled={busy}
                    onClick={() => handleSingle(m.user_id, true)}
                  >
                    <UserMinus size={14} aria-hidden="true" /> Downgrade to Member
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {error ? <div className="owner-review-banner__error">{error}</div> : null}
    </div>
  );
};

export default OwnerReviewBanner;
